<template>
  <highcharts
    v-if="chartOptions"
    ref="chart"
    type="chart"
    :options="chartOptions"
  />
</template>
<script>
import { mapGetters } from 'vuex';
import { roles } from 'src/config/roles-config.js';
import {
  GOOD_CYCLE_COLOR,
  BAD_CYCLE_COLOR,
} from 'src/config/charts-config.js';
export default {
  inject: ['playCycle'],
  props: {
    options: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    isFilteredOutliers: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  data() {
    return {
      chartOptions: null,
      filteredData: [],
      outLierLimit: 600,
      loading: false,
    };
  },

  computed: {
    ...mapGetters(['showCompareAnalytics', 'role']),
    // Support users see cycles colored by outcome (good/bad) instead of traced/untraced.
    colorByStatus() {
      return this.role === roles.support_user;
    },
  },

  watch: {
    isFilteredOutliers(value) {
      const { data } = this.options;
      if (value) {
        this.filteredData = data.filter((el) => el < this.outLierLimit);
      } else {
        this.filteredData = data;
      }
      this.setChartOptions();
    },

    chartOptions() {
      const { data } = this.options;
      if (!data) return;
      this.filteredData = this.options.data.filter(
        (el) => el < this.outLierLimit
      );
    },
  },

  mounted() {
    const { data } = this.options;
    this.filteredData = data;
    this.setChartOptions();
  },

  methods: {
    setChartOptions() {
      const self = this;
      const { labels, xlabelString, ylabelString } = this.options;

      this.chartOptions = {
        chart: {
          zoomType: 'xy',
          height: this.showCompareAnalytics ? '70%' : '50%',
        },
        exporting: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        legend: {
          enabled: true,
          symbolWidth: 10,
        },
        yAxis: {
          title: {
            text: ylabelString,
          },
        },
        xAxis: {
          categories: labels,
          title: {
            text: xlabelString,
          },
        },
        plotOptions: {
          series: {
            turboThreshold: 1000000,
            states: {
              hover: {
                enabled: true,
                lineWidth: 0.5,
              },
            },
          },
          line: {
            events: {
              legendItemClick: function () {
                return false;
              },
            },
          },
        },
        tooltip: {
          formatter: function () {
            const { x, y, point } = this;
            const isGoodCycle = point?.custom?.isGoodCycle;
            const statusLine =
              isGoodCycle === undefined
                ? ''
                : `<br/><span>Status: </span><b>${
                    isGoodCycle ? 'Good' : 'Bad'
                  }</b>`;
            return `<span>Date: </span><b>${x}</b>
                <br/><span>Cycle Time: </span><b>${y}s</b>${statusLine}`;
          },
        },
        series: [
          {
            name: this.colorByStatus ? 'Good Cycle' : 'Traced Cycle',
            type: 'line',
            ...(this.colorByStatus && {
              color: GOOD_CYCLE_COLOR,
              zoneAxis: 'x',
              zones: this.getBadCycleZones(this.filteredData),
            }),
            data: this.filteredData,
            dataGrouping: {
              enabled: true,
            },
            lineWidth: 0.8,
            label: { enabled: false },
            point: {
              events: {
                click: function () {
                  const { category: cycleIdentifier, custom } = this;
                  if (!custom?.isTraced) return;
                  self.playCycle(cycleIdentifier);
                },
              },
            },
          },
          this.colorByStatus
            ? { name: 'Bad Cycle', color: BAD_CYCLE_COLOR }
            : { name: 'Untraced Cycle', color: 'lightgray' },
        ],
      };
    },

    // Color the line segment leading into a bad cycle red. Zones apply to
    // x < value, so [i-1, i) gets the bad color; points keep their own color.
    getBadCycleZones(data) {
      const zones = [];
      if (!Array.isArray(data)) return zones;
      data.forEach((point, index) => {
        const isGoodCycle = point?.custom?.isGoodCycle;
        // Same rule as the tooltip/point color: any falsy status counts as bad.
        if (index === 0 || isGoodCycle === undefined || isGoodCycle) return;
        zones.push(
          { value: index - 1, color: GOOD_CYCLE_COLOR },
          { value: index, color: BAD_CYCLE_COLOR }
        );
      });
      return zones;
    },
  },
};
</script>
